import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { JourneySmall } from '@interfaces/central';
import { AddressDetail } from '@interfaces/rest/interfaces/journey';
import { LocationService } from '@services/location.service';
import { DirectionService } from '@services/direction.service';
import { Direction } from '@interfaces/directions';
import { TranslateService } from '@ngx-translate/core';

export interface DestinationData {
  origin: {
    address: AddressDetail;
    lat: number;
    lng: number;
  } | null;
  destination: {
    address: AddressDetail;
    lat: number;
    lng: number;
  } | null;
}

@Component({
  selector: 'app-call-destination',
  templateUrl: './call-destination.component.html',
  styleUrls: ['./call-destination.component.scss'],
})
export class CallDestinationComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() lastJourney: JourneySmall[] = [];
  @Output() destinationChanged = new EventEmitter<DestinationData>();

  // Exponer los contenedores como públicos para que el padre pueda acceder a ellos si necesita limpiar inputs
  @ViewChild('originContainer') originContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('destinationContainer') destinationContainer!: ElementRef<HTMLDivElement>;

  selectedOriginIndex: number | null = null;
  hasUserAddresses: boolean = false; // Flag simple: true si hay direcciones de usuario
  displayJourneys: JourneySmall[] = []; // Array que se muestra en el template

  // Textos para mostrar la dirección seleccionada (necesarios porque PlaceAutocompleteElement no permite establecer valores programáticamente)
  selectedOriginText: string = '';
  selectedDestinationText: string = '';

  // Google Autocomplete objects
  private originAutocomplete: any;
  private destinationAutocomplete: any;

  // Búsqueda interna
  useInternalSearchOrigin: boolean = false;
  useInternalSearchDestination: boolean = false;
  internalSearchTextOrigin: string = '';
  internalSearchTextDestination: string = '';
  internalDirectionsOrigin: Direction[] = [];
  internalDirectionsDestination: Direction[] = [];
  showInternalOrigin: boolean = false;
  showInternalDestination: boolean = false;

  currentDestination: DestinationData = {
    origin: null,
    destination: null
  };

  constructor(
    private locationService: LocationService,
    private directionService: DirectionService,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    this.processJourneyList();
  }

  ngAfterViewInit() {
    // Inicializar Google Autocomplete después de que la vista esté lista
    setTimeout(() => {
      this.initializeGoogleAutocomplete();
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['lastJourney']) {
      this.processJourneyList();
    }
  }

  private processJourneyList() {
    // Simple: si el input tiene datos, son direcciones de usuario
    const hasUserData = this.lastJourney.length > 0;
    this.hasUserAddresses = hasUserData;

    // Si no hay direcciones desde la API, usar las direcciones por defecto
    if (!hasUserData) {
      this.displayJourneys = [
        {
          fromAddress: {
            title: 'Aeropuerto de Mallorca',
            description: 'Aeropuerto de Mallorca'
          },
          fromLat: 39.551623,
          fromLng: 2.738925
        },
        {
          fromAddress: {
            title: 'Palma centro',
            description: 'Centre, Palma'
          },
          fromLat: 39.572398,
          fromLng: 2.650832
        }
      ];
      return; // No procesamos más si son direcciones default
    }

    // Si hay datos de usuario, procesarlos
    // Eliminar direcciones duplicadas que tienen el mismo lat y lng
    let processedJourneys = this.lastJourney.filter((journey, index, self) => {
      return self.findIndex(j => j.fromLat === journey.fromLat && j.fromLng === journey.fromLng) === index;
    });

    // Filtrar direcciones que no sean "Otra dirección" para aplicar el límite
    const regularJourneys = processedJourneys.filter(journey =>
      !(journey.fromLat === 0 && journey.fromLng === 0 && journey.fromAddress.title === 'Otra dirección')
    );

    // Limitar a máximo 5 direcciones regulares para dejar espacio para "Otra dirección"
    const maxRegularJourneys = 5;
    const limitedJourneys = regularJourneys.slice(0, maxRegularJourneys);

    // Añadir "Otra dirección" al final
    this.displayJourneys = [
      ...limitedJourneys,
      {
        fromAddress: {
          title: 'Otra dirección',
          description: ''
        },
        fromLat: 0,
        fromLng: 0
      }
    ];

    // Seleccionar automáticamente el primer elemento si no hay ninguna selección previa
    if (this.selectedOriginIndex === null) {
      this.selectedOriginIndex = 0;
      this.onOriginAddressSelected(0);
    }
  }

  onOriginRadioChange(event: any) {
    const index = event.detail.value;
    this.onOriginAddressSelected(index);
  }

  onOriginAddressSelected(index: number) {
    this.selectedOriginIndex = index;
    const selectedJourney = this.displayJourneys[index];

    if (selectedJourney.fromLat === 0 && selectedJourney.fromLng === 0) {
      // "Otra dirección" seleccionada - limpiar
      this.currentDestination.origin = null;
      this.selectedOriginText = '';
    } else {
      // Dirección específica seleccionada
      this.currentDestination.origin = {
        address: selectedJourney.fromAddress,
        lat: selectedJourney.fromLat,
        lng: selectedJourney.fromLng
      };
      // Actualizar el texto mostrado (PlaceAutocompleteElement no permite establecer valores programáticamente)
      this.selectedOriginText = selectedJourney.fromAddress.description
        ? `${selectedJourney.fromAddress.title}, ${selectedJourney.fromAddress.description}`
        : selectedJourney.fromAddress.title;
    }

    this.emitDestinationChange();
  }

  private initializeGoogleAutocomplete() {
    // Solo inicializar Google Autocomplete si no está activada la búsqueda interna
    if (!this.useInternalSearchOrigin && this.originContainer?.nativeElement) {
      this.originAutocomplete = this.locationService.setupAutocomplete(
        this.originContainer.nativeElement,
        (data) => this.handleOriginSelected(data)
      );
    }

    if (!this.useInternalSearchDestination && this.destinationContainer?.nativeElement) {
      this.destinationAutocomplete = this.locationService.setupAutocomplete(
        this.destinationContainer.nativeElement,
        (data) => this.handleDestinationSelected(data)
      );
    }
  }

  /**
   * Maneja el cambio del checkbox de búsqueda interna para origen
   */
  onInternalSearchOriginChange(event: any) {
    this.useInternalSearchOrigin = event.detail.checked;
    if (this.useInternalSearchOrigin) {
      // Destruir Google Autocomplete si existe
      if (this.originAutocomplete) {
        this.locationService.destroyAutocomplete(this.originAutocomplete);
        this.originAutocomplete = undefined;
      }
      // Limpiar selección de Google
      this.selectedOriginText = '';
      this.currentDestination.origin = null;
      this.selectedOriginIndex = null;
    } else {
      // Limpiar búsqueda interna y selección
      this.internalSearchTextOrigin = '';
      this.internalDirectionsOrigin = [];
      this.showInternalOrigin = false;
      this.selectedOriginText = '';
      this.currentDestination.origin = null;
      // Reinicializar Google Autocomplete
      setTimeout(() => {
        if (this.originContainer?.nativeElement) {
          this.originAutocomplete = this.locationService.setupAutocomplete(
            this.originContainer.nativeElement,
            (data) => this.handleOriginSelected(data)
          );
        }
      }, 100);
    }
    this.emitDestinationChange();
  }

  /**
   * Maneja el cambio del checkbox de búsqueda interna para destino
   */
  onInternalSearchDestinationChange(event: any) {
    this.useInternalSearchDestination = event.detail.checked;
    if (this.useInternalSearchDestination) {
      // Destruir Google Autocomplete si existe
      if (this.destinationAutocomplete) {
        this.locationService.destroyAutocomplete(this.destinationAutocomplete);
        this.destinationAutocomplete = undefined;
      }
      // Limpiar selección de Google
      this.selectedDestinationText = '';
      this.currentDestination.destination = null;
    } else {
      // Limpiar búsqueda interna y selección
      this.internalSearchTextDestination = '';
      this.internalDirectionsDestination = [];
      this.showInternalDestination = false;
      this.selectedDestinationText = '';
      this.currentDestination.destination = null;
      // Reinicializar Google Autocomplete
      setTimeout(() => {
        if (this.destinationContainer?.nativeElement) {
          this.destinationAutocomplete = this.locationService.setupAutocomplete(
            this.destinationContainer.nativeElement,
            (data) => this.handleDestinationSelected(data)
          );
        }
      }, 100);
    }
    this.emitDestinationChange();
  }

  /**
   * Busca en direcciones internas cuando se escribe en el input
   */
  onInternalSearchInput(type: 'origin' | 'destination', event: any) {
    const searchText = event.target?.value || '';
    this.searchInternalDirections(searchText, type);
  }

  /**
   * Maneja el blur del input de búsqueda interna
   */
  onInternalSearchBlur(type: 'origin' | 'destination') {
    // Usar setTimeout para permitir que el click en el dropdown se registre antes de ocultarlo
    setTimeout(() => {
      if (type === 'origin') {
        this.showInternalOrigin = false;
      } else {
        this.showInternalDestination = false;
      }
    }, 300);
  }

  /**
   * Busca en direcciones internas usando el servicio
   */
  private searchInternalDirections(searchText: string, type: 'origin' | 'destination') {
    if (type === 'origin') {
      this.internalSearchTextOrigin = searchText;
    } else {
      this.internalSearchTextDestination = searchText;
    }

    if (!searchText || !searchText.trim()) {
      if (type === 'origin') {
        this.internalDirectionsOrigin = [];
        this.showInternalOrigin = false;
      } else {
        this.internalDirectionsDestination = [];
        this.showInternalDestination = false;
      }
      return;
    }

    // Usar el método de búsqueda del servicio (limitar a 5 resultados)
    const matchedDirections = this.directionService.searchDirections(searchText, 5);

    if (type === 'origin') {
      this.internalDirectionsOrigin = matchedDirections;
      this.showInternalOrigin = matchedDirections.length > 0;
    } else {
      this.internalDirectionsDestination = matchedDirections;
      this.showInternalDestination = matchedDirections.length > 0;
    }
  }

  /**
   * Selecciona una dirección interna como origen
   */
  selectInternalOrigin(direction: Direction) {
    this.currentDestination.origin = {
      address: {
        title: direction.title,
        description: direction.description || direction.fromAddress
      },
      lat: direction.fromLat,
      lng: direction.fromLng
    };
    this.selectedOriginIndex = null; // Deseleccionar radio button
    this.selectedOriginText = direction.description
      ? `${direction.title}, ${direction.description}`
      : direction.title;
    this.showInternalOrigin = false;
    this.internalSearchTextOrigin = direction.title;
    this.internalDirectionsOrigin = [];
    this.emitDestinationChange();
  }

  /**
   * Selecciona una dirección interna como destino
   */
  selectInternalDestination(direction: Direction) {
    this.currentDestination.destination = {
      address: {
        title: direction.title,
        description: direction.description || direction.fromAddress
      },
      lat: direction.fromLat,
      lng: direction.fromLng
    };
    this.selectedDestinationText = direction.description
      ? `${direction.title}, ${direction.description}`
      : direction.title;
    this.showInternalDestination = false;
    this.internalSearchTextDestination = direction.title;
    this.internalDirectionsDestination = [];
    this.emitDestinationChange();
  }

  // Handlers para cuando se selecciona un lugar en Google Autocomplete
  private handleOriginSelected(data: any) {
    this.currentDestination.origin = this.processPlaceData(data);
    this.selectedOriginIndex = null; // Deseleccionar radio button
    // Actualizar el texto mostrado con la dirección de Google
    this.selectedOriginText = data.formatted_address ||
      (data.street ? `${data.street}, ${data.city}` : data.city) || '';
    this.emitDestinationChange();
  }

  private handleDestinationSelected(data: any) {
    this.currentDestination.destination = this.processPlaceData(data);
    // Actualizar el texto mostrado con la dirección de Google
    this.selectedDestinationText = data.formatted_address ||
      (data.street ? `${data.street}, ${data.city}` : data.city) || '';
    this.emitDestinationChange();
  }

  /**
   * Procesa los datos del lugar seleccionado y devuelve el objeto formateado
   * Si existe un nombre de lugar (hotel, hospital, etc.), lo usa como título.
   * Si no, usa la calle como título (comportamiento por defecto).
   */
  private processPlaceData(data: any): { address: AddressDetail; lat: number; lng: number } {
    let title: string;
    let description: string;

    if (data.place_name && data.place_name.trim() !== '' && data.place_name !== data.street) {
      // Si existe un nombre de lugar (hotel, hospital, etc.), usarlo como título
      title = data.place_name;
      description = data.street || data.formatted_address || data.city;
    } else {
      // Si no existe nombre de lugar, usar la calle como título (comportamiento original)
      title = data.street;
      description = data.city;
    }

    return {
      address: { title, description },
      lat: data.lat,
      lng: data.lng
    };
  }

  ngOnDestroy() {
    // Limpiar autocomplete al destruir el componente
    if (this.originAutocomplete) {
      this.locationService.destroyAutocomplete(this.originAutocomplete);
    }

    if (this.destinationAutocomplete) {
      this.locationService.destroyAutocomplete(this.destinationAutocomplete);
    }
  }

  private emitDestinationChange() {
    this.destinationChanged.emit(this.currentDestination);
  }

  /**
   * Limpia el origen y destino seleccionados
   * Restablece el estado del componente y limpia los Web Components de Google Places
   */
  clear() {
    // Limpiar estado interno
    this.selectedOriginIndex = null;
    this.currentDestination = {
      origin: null,
      destination: null
    };
    this.selectedOriginText = '';
    this.selectedDestinationText = '';

    // Limpiar búsqueda interna
    this.internalSearchTextOrigin = '';
    this.internalSearchTextDestination = '';
    this.internalDirectionsOrigin = [];
    this.internalDirectionsDestination = [];
    this.showInternalOrigin = false;
    this.showInternalDestination = false;

    // Limpiar los Web Components de Google Places (PlaceAutocompleteElement)
    // El PlaceAutocompleteElement tiene Shadow DOM, necesitamos acceder al input interno
    this.clearPlaceAutocomplete(this.originAutocomplete, this.originContainer);
    this.clearPlaceAutocomplete(this.destinationAutocomplete, this.destinationContainer);

    // Emitir cambio para notificar al componente padre
    this.emitDestinationChange();
  }

  /**
   * Limpia un PlaceAutocompleteElement accediendo al input dentro de su Shadow DOM
   */
  private clearPlaceAutocomplete(autocompleteElement: any, container: ElementRef<HTMLDivElement> | undefined) {
    // Intentar con la instancia almacenada primero
    let placeElement = autocompleteElement;

    // Si no tenemos la instancia, buscar el elemento en el DOM
    if (!placeElement && container?.nativeElement) {
      placeElement = container.nativeElement.querySelector('gmp-place-autocomplete');
    }

    if (placeElement) {
      try {
        // Método 0: Intentar usar métodos públicos del componente si existen
        if (typeof (placeElement as any).reset === 'function') {
          (placeElement as any).reset();
          return;
        }
        if (typeof (placeElement as any).clear === 'function') {
          (placeElement as any).clear();
          return;
        }

        // Método 1: Intentar acceder al input dentro del Shadow DOM (si está abierto)
        if (placeElement.shadowRoot) {
          const input = placeElement.shadowRoot.querySelector('input');
          if (input) {
            input.value = '';
            // Disparar eventos para asegurar que el componente detecte el cambio
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
            // También disparar eventos de teclado para simular un borrado completo
            input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
            return;
          }
        }

        // Método 2: Intentar establecer value directamente en el elemento
        if (typeof (placeElement as any).value !== 'undefined') {
          (placeElement as any).value = '';
          return;
        }

        // Método 3: Si el Shadow DOM está cerrado, intentar acceder usando el método de acceso
        // Algunos Web Components exponen el input a través de una propiedad
        const inputElement = (placeElement as any).inputElement || (placeElement as any).input;
        if (inputElement) {
          inputElement.value = '';
          inputElement.dispatchEvent(new Event('input', { bubbles: true }));
          return;
        }

        // Método 4: Buscar el input dentro del contenedor (puede estar fuera del Shadow DOM en algunos casos)
        if (container?.nativeElement) {
          const input = container.nativeElement.querySelector('input');
          if (input) {
            input.value = '';
            input.dispatchEvent(new Event('input', { bubbles: true }));
            return;
          }
        }

        // Método 5: Si todo lo demás falla, recrear el componente
        // Esto es más costoso pero garantiza que el componente esté limpio
        if (container?.nativeElement) {
          // Determinar si es origen o destino basándose en el contenedor
          const isOrigin = container === this.originContainer;
          console.warn('Recreando PlaceAutocompleteElement para limpiarlo');
          this.reinitializeAutocomplete(container, isOrigin);
        } else {
          console.warn('No se pudo encontrar el input para limpiar el autocomplete');
        }
      } catch (error) {
        console.warn('Error al limpiar autocomplete:', error);
      }
    }
  }

  /**
   * Re-inicializa un autocomplete recreando el componente
   */
  private reinitializeAutocomplete(container: ElementRef<HTMLDivElement>, isOrigin: boolean) {
    if (!container?.nativeElement) return;

    // Destruir el autocomplete existente
    if (isOrigin) {
      if (this.originAutocomplete) {
        this.locationService.destroyAutocomplete(this.originAutocomplete);
      }
      this.originAutocomplete = undefined;
    } else {
      if (this.destinationAutocomplete) {
        this.locationService.destroyAutocomplete(this.destinationAutocomplete);
      }
      this.destinationAutocomplete = undefined;
    }

    // Limpiar el contenedor
    container.nativeElement.innerHTML = '';

    // Recrear el autocomplete
    setTimeout(() => {
      if (isOrigin) {
        this.originAutocomplete = this.locationService.setupAutocomplete(
          container.nativeElement,
          (data) => this.handleOriginSelected(data)
        );
      } else {
        this.destinationAutocomplete = this.locationService.setupAutocomplete(
          container.nativeElement,
          (data) => this.handleDestinationSelected(data)
        );
      }
    }, 0);
  }
}
